import { Suspense, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loading } from '../../components/ui/index.jsx';
import { ARTICLES, articleBySlug, articleComponent } from './articles/index.js';

/** "On this page": the article's sections, found once it has rendered. */
function useSections(ref, key) {
  const [sections, setSections] = useState([]);
  useEffect(() => {
    const read = () => setSections([...(ref.current?.querySelectorAll('section.help-section') || [])]
      .map((s) => ({ id: s.id, title: s.querySelector('h2')?.textContent || '' })));
    read();
    const obs = new MutationObserver(read);
    if (ref.current) obs.observe(ref.current, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [ref, key]);
  return sections;
}

export default function HelpArticle() {
  const { slug } = useParams();
  const article = articleBySlug(slug);
  const Body = articleComponent(slug);
  const body = useRef(null);
  const sections = useSections(body, slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!article) {
    return (
      <div className="page">
        <Link to="/help" className="back-link">← Help centre</Link>
        <h1>We couldn’t find that article</h1>
        <p>It may have moved. Have a look in the <Link to="/help">help centre</Link>.</p>
      </div>
    );
  }
  const related = ARTICLES.filter((a) => a.group === article.group && a.slug !== slug);

  return (
    <div className="help-layout">
      <article className="help-main">
        <Link to="/help" className="back-link">← Help centre</Link>
        <h1 className="help-title">{article.title}</h1>
        <div ref={body} className="help-article">
          <Suspense fallback={<Loading />}><Body /></Suspense>
        </div>
      </article>
      <aside className="help-side">
        <nav className="help-toc" aria-label="On this page">
          <p className="help-toc-title">On this page</p>
          <ol>
            {sections.map((s) => <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>)}
          </ol>
          {article.page && <Link to={article.page} className="btn btn-secondary btn-small btn-block">Go to the page</Link>}
        </nav>
        {related.length > 0 && (
          <nav className="help-related" aria-label="Related help">
            <p className="help-toc-title">Related</p>
            <ul>{related.map((a) => <li key={a.slug}><Link to={`/help/${a.slug}`}>{a.title}</Link></li>)}</ul>
          </nav>
        )}
      </aside>
    </div>
  );
}
